
import { Box, Divider, Stack, Typography } from "@mui/material";
import SectionTabs from "../SectionTabs";
import CompositionHeader from "../CompositionHeader";
import { PatientCard, PractitionerCard, OrganizationSection, HospitalBanner } from "../InfoCards";
import EncounterSection from "../EncounterSection";
import DocumentReferenceSection from "../DocumentReferenceSection";
import { labelSx, valueSx, formatDate } from "../styles";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const ImmunizationView = ({ bundle, setShowPdfPreview }: { bundle: any; setShowPdfPreview: (v: boolean) => void }) => {
    const composition = bundle?.composition || {};
    const patient = bundle || {};
    const allergies = bundle?.allergyIntolerances || [];
    const immunizations = bundle?.processedImmunization || bundle?.immunizations || [];

    const sections = [
        {
            id: "immunizations",
            label: "Immunizations",
            content:
                immunizations.length === 0 ? (
                    <Typography sx={labelSx}>No immunizations recorded.</Typography>
                ) : (
                    <Stack spacing={1.5}>
                        {immunizations.map((imm: any, idx: number) => (
                            <Box
                                key={`immunization-${idx}`}
                                sx={{
                                    p: 2,
                                    border: "2px solid #f0f0f0",
                                    borderRadius: 2.5,
                                    bgcolor: "linear-gradient(135deg, #fafafa 0%, #f5fffe 100%)",
                                    boxShadow: "0 4px 16px rgba(129,196,187,0.12)",
                                    transition: "all 0.35s cubic-bezier(0.4, 0, 0.2, 1)",
                                    "&:hover": {
                                        boxShadow: "0 8px 24px rgba(129,196,187,0.2)",
                                        borderColor: "#2fbf71",
                                        bgcolor: "#fff",
                                        transform: "translateY(-2px)"
                                    }
                                }}
                            >
                                <Typography sx={valueSx}>{imm.vaccineCodeText || imm.vaccineCode || "Immunization"}</Typography>
                                <Box
                                    sx={{
                                        mt: 0.5,
                                        display: "grid",
                                        gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", lg: "repeat(4, 1fr)" },
                                        gap: 2
                                    }}
                                >
                                    <Box sx={{ display: "flex", flexDirection: "column" }}>
                                        <Typography sx={labelSx}>Name</Typography>
                                        <Typography sx={valueSx}>{imm.vaccineName || "—"}</Typography>
                                    </Box>
                                    <Box sx={{ display: "flex", flexDirection: "column" }}>
                                        <Typography sx={labelSx}>Status</Typography>
                                        <Typography sx={valueSx}>{imm.status || "—"}</Typography>
                                    </Box>
                                    <Box sx={{ display: "flex", flexDirection: "column" }}>
                                        <Typography sx={labelSx}>Date</Typography>
                                        <Typography sx={valueSx}>{formatDate(imm.occurrenceDateTime)}</Typography>
                                    </Box>
                                    {imm.routeText ? (
                                        <Box sx={{ display: "flex", flexDirection: "column" }}>
                                            <Typography sx={labelSx}>Route</Typography>
                                            <Typography sx={valueSx}>{imm.routeText}</Typography>
                                        </Box>
                                    ) : null}
                                    {imm.lotNumber ? (
                                        <Box sx={{ display: "flex", flexDirection: "column" }}>
                                            <Typography sx={labelSx}>Lot</Typography>
                                            <Typography sx={valueSx}>{imm.lotNumber}</Typography>
                                        </Box>
                                    ) : null}
                                </Box>
                            </Box>
                        ))}
                    </Stack>
                )
        },
        {
            id: "allergies",
            label: "Allergies",
            content:
                allergies.length === 0 ? (
                    <Typography sx={labelSx}>No allergies recorded.</Typography>
                ) : (
                    <Stack spacing={1.5}>
                        {allergies.map((allergy: any, idx: number) => (
                            <Box key={`allergy-${idx}`} sx={{ p: 1.5, border: "1px dashed", borderColor: "divider", borderRadius: 1 }}>
                                <Typography sx={valueSx}>{allergy.allergyDisplay || "Allergy"}</Typography>
                                <Box
                                    sx={{
                                        mt: 0.5,
                                        display: "grid",
                                        gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", lg: "repeat(4, 1fr)" },
                                        gap: 2
                                    }}
                                >
                                    <Box sx={{ display: "flex", flexDirection: "column" }}>
                                        <Typography sx={labelSx}>Clinical Status</Typography>
                                        <Typography sx={valueSx}>{allergy.clinicalStatus || "—"}</Typography>
                                    </Box>
                                    <Box sx={{ display: "flex", flexDirection: "column" }}>
                                        <Typography sx={labelSx}>Verification Status</Typography>
                                        <Typography sx={valueSx}>{allergy.verificationStatus || "—"}</Typography>
                                    </Box>
                                    <Box sx={{ display: "flex", flexDirection: "column" }}>
                                        <Typography sx={labelSx}>Authored</Typography>
                                        <Typography sx={valueSx}>{formatDate(allergy.authoredDate)}</Typography>
                                    </Box>
                                </Box>
                            </Box>
                        ))}
                    </Stack>
                )
        },
        {
            id: "attachments",
            label: "Attachments",
            content: bundle?.documentReference || bundle?.binary?.data ? (
                <DocumentReferenceSection
                    documentReference={bundle?.documentReference}
                    binary={bundle?.binary}
                    onPreviewBase64={() => setShowPdfPreview(true)}
                />
            ) : (
                <Typography sx={labelSx}>No attachments available.</Typography>
            )
        }
    ];

    return (
        <Stack spacing={3}>
            <HospitalBanner organization={bundle?.organization} />
            <CompositionHeader composition={composition} />

            <Stack direction={{ xs: "column", md: "row" }} spacing={2} sx={{ mt: "8px !important" }}>
                <PatientCard patient={patient} />
                <PractitionerCard practitioner={patient} />
            </Stack>

            <OrganizationSection organization={bundle?.organization} />
            <EncounterSection encounter={bundle?.encounter} />

            <Divider />
            <SectionTabs sections={sections} initialId="immunizations" />
        </Stack>
    );
};

export default ImmunizationView;
